/** Builds and orders a complete brand-item cohort from DataView membership and metadata. */
import type {LocalizationEntry} from "../../../Queries";
import {
   DataViewAnnotations,
   DataViewColumn,
   DataViewColumnType,
   DataViewRowValues,
   DataViewSortDirection
} from "../../../components/dataview/DataViewColumns";

export interface CohortRow {
   id: string;
   sourceIndex: number;
   values: DataViewRowValues;
}

export interface CohortSort {
   key: string;
   direction: DataViewSortDirection;
}

/** Creates one row per unique DataView member and joins its producer metadata. */
export const buildCohortRows = (
   rowIds: string[],
   annotations: DataViewAnnotations | null
): CohortRow[] => {
   const uniqueIds = rowIds.filter((id, index) => rowIds.indexOf(id) === index);
   return uniqueIds.map((id, sourceIndex) => ({
      id,
      sourceIndex,
      values: annotations?.rows[id] ?? {}
   }));
};

/** Chooses the producer sort, then Age descending, or leaves source order intact. */
export const defaultCohortSort = (annotations: DataViewAnnotations | null): CohortSort | undefined => {
   if (annotations?.defaultSort) {
      return {...annotations.defaultSort};
   }
   const ageColumn = annotations?.columns.find(column => column.key.toLocaleLowerCase() === "age");
   return ageColumn ? {key: ageColumn.key, direction: "DESC"} : undefined;
};

/** Returns whether a metadata value should sort after every usable value. */
const isMissingSortValue = (
   value: string | number | null | undefined,
   column: DataViewColumn
): boolean => value === null
   || value === undefined
   || (column.dataType !== DataViewColumnType.TEXT && (typeof value !== "number" || !Number.isFinite(value)));

/** Compares two present metadata values according to the column's declared type. */
const comparePresentValues = (
   left: string | number,
   right: string | number,
   column: DataViewColumn
): number => column.dataType === DataViewColumnType.TEXT
   ? String(left).localeCompare(String(right), undefined, {numeric: true, sensitivity: "base"})
   : Number(left) - Number(right);

/** Sorts the full cohort without mutating it, keeping missing values last and ties stable. */
export const sortCohortRows = (
   rows: CohortRow[],
   columns: DataViewColumn[],
   sort?: CohortSort
): CohortRow[] => {
   if (!sort) {
      return [...rows];
   }
   const column = columns.find(candidate => candidate.key === sort.key);
   if (!column) {
      return [...rows];
   }

   return [...rows].sort((left, right) => {
      const leftValue = left.values[column.key];
      const rightValue = right.values[column.key];
      const leftMissing = isMissingSortValue(leftValue, column);
      const rightMissing = isMissingSortValue(rightValue, column);

      if (leftMissing !== rightMissing) {
         return leftMissing ? 1 : -1;
      }
      if (leftMissing && rightMissing) {
         return left.sourceIndex - right.sourceIndex;
      }

      const compared = comparePresentValues(leftValue!, rightValue!, column);
      return compared === 0
         ? left.sourceIndex - right.sourceIndex
         : compared * (sort.direction === "ASC" ? 1 : -1);
   });
};

/** Reads a localized value with first-non-empty and caller-provided fallbacks. */
export const localizedValue = (
   values: Array<Pick<LocalizationEntry, "locale" | "value"> | null> | null | undefined,
   locale: string | null | undefined,
   fallback: string
): string => values?.find(entry => entry?.locale === locale && entry.value.trim())?.value
   ?? values?.find(entry => entry?.value.trim())?.value
   ?? fallback;

/** Moves by one row while staying inside the cohort boundaries. */
export const nextCohortId = (
   ids: string[],
   selectedId: string | undefined,
   delta: -1 | 1
): string | undefined => {
   if (!ids.length) {
      return undefined;
   }
   const current = Math.max(0, ids.indexOf(selectedId ?? ids[0]));
   return ids[Math.min(ids.length - 1, Math.max(0, current + delta))];
};
